/*
* Portal Users: Reserve Study scenario form
*/
import './report.css';

import * as _ from 'lodash';
import moment from 'moment';
import * as React from 'react';
import { Alert, Button, ButtonToolbar, Modal, Panel, Row } from 'react-bootstrap';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { push } from 'react-router-redux';
import { compose } from 'redux';
import { connectRequest } from 'redux-query';
import { ButtonGroup, Checkbox } from 'rsuite';

import * as MutateActions from '../actions/mutate';
import Auth from '../auth/auth';
import * as AppComp from '../common/app-component-base';
import * as AppUtilities from '../common/apputilities';
import { BootstrapColXl as Col } from '../common/bootstrap/bootstrapcolxl';
import { Formatters } from '../common/formatters';
import { GridUtilities } from '../common/grid/gridutilities';
import * as KendoWrappers from '../common/kendoforms/componentwrappers';
import { NavigationLinks } from '../common/navigation';
import { WhiteBoxPageSection } from '../common/whiteboxpagesection';
import ApiEndpoints from '../config/apiendpoints';
import AppConfiguration from '../config/configuration';
import { PortalAccessActions, PortalRoles } from '../invite/create-invite';
import * as ReduxQueries from '../queries';
import { UserUtilities } from '../user/user-utilities';
import * as ReportCharts from './charts';
import * as ReportInventoryGrids from './inventorygrids';
import * as PortalReportLogic from './logic';
import * as PercentFundedDisplay from './percentfundedsection';
import * as PortalChartForm from './portal-chartform';
import * as ReportCommon from './report-common';

export const PortalReserveStudyFormSection = ({ title, className = '', ...rest }) => {
    return (
        <div className={`reserve-study-form-section ${className}`}>
            <KendoWrappers.KendoFormSection title={title}>
                {rest.children}
            </KendoWrappers.KendoFormSection>
        </div>
    );
};

export interface IPortalAssociationReportProps extends AppComp.IAppComponentProps {
    association?: any;
    associationId?: any;
    currentOrder?: any;
    navigateTo?: any;
    currentOrderChart?: any;
    inventory?: any;
    scenarios?: any;
    portalInvites?: any;
    visibleScenarios?: any;
    toggleClientInvite?: any;
    resendClientInvite?: any;
    updateReportScenario?: any;
    copyReportScenario?: any;
    deleteReportScenario?: any;
}

export interface IPortalAssociationReportState extends AppComp.IAppComponentState {
    formChanges?: any;
    inventoryChanges?: any;
    showModalToggleInvite: boolean;
    showModalOptimizeScenario: boolean;
    showModalAlreadyOptimized: boolean;
    modalOptimizeBusy: boolean;
    modalOptimizeDismiss: boolean;
    toggleInvite?: any;
    showModalSaveChanges?: boolean;
    showPanelScenarios?: boolean;
    gridKey?: string;
}

class PortalAssociationReport extends AppComp.AppComponent<IPortalAssociationReportProps, IPortalAssociationReportState> {
    public originalExtendedInventoryCache: Array<any> = [];
    public originalFormDataCache = {};
    public readonly panelScenariosDefaultExpanded: boolean = false;
    public readonly panelManageUsersDefaultExpanded: boolean = false;

    public constructor(props: IPortalAssociationReportProps) {
        super(props, 'app.report-association.');

        this.state = {
            formChanges: {},
            inventoryChanges: {},
            showModalToggleInvite: false,
            showModalOptimizeScenario: false,
            showModalAlreadyOptimized: false,
            modalOptimizeBusy: false,
            modalOptimizeDismiss: false,
            toggleInvite: {},
            showModalSaveChanges: false,
            showPanelScenarios: (this.panelScenariosDefaultExpanded) ? true : false,
            gridKey: new Date().getTime().toString(),
        };

        ReportCharts.setHighchartOptions();
    }

    // EXTENDERS
    public extendAssociationData(record: any) {
        if (!record) { return; }

        let result = _.cloneDeep(record);
        const invSubgroups = AppUtilities.getArray(record, 'inventorySubgroups');
        if (AppUtilities.isArrayWithLength(invSubgroups)) {
            // result.inventorySubgroups.push({ groupCode: '', title: '', label: this.appText('filter-option-any') });
            result.inventorySubgroups = result.inventorySubgroups.map(c => {
                if (!c.label) {
                    c.label = Formatters.composeInventorySubgroupLabel(c, true); // c.label = `${c.groupCode} - ${c.title}`;
                }

                return c;
            });
        }

        return result;
    }

    public extendInventoryData(inventory: Array<any>, inventoryChangesIn: any, association: any, scenarioInventoryIn: Array<any>) {
        // Extend grid row data
        if (!AppUtilities.isArrayWithLength(inventory)) { return []; }

        const enableTracing: boolean = false;
        const msgLogPrefix = `portal.tsx::extendInventoryData`;

        const extended = inventory.map((r, i) => {
            let record = _.cloneDeep(r);

            record.avgCost = PortalReportLogic.calculateInventoryItemAvgCost(record);
            record.avgCost_sort = Number(record.avgCost);
            if (!record.avgCost) {
                GridUtilities.recordSetRowClass(record, 'row-no-avg-cost');
                record.usefulLife = 'N/A';
                record.remainingUsefulLife = 0;
            }

            const invSubgroups = AppUtilities.getArray(association, 'inventorySubgroups');
            record.Group = '';
            if (AppUtilities.isArrayWithLength(invSubgroups)) {
                let subgroupDef: any = _.find(invSubgroups, ['groupCode', _.get(record, 'subgroup', '')]);
                if (subgroupDef && subgroupDef.title) { record.Group = subgroupDef.title; }
            }

            record.usefulLife_sort = (AppUtilities.isNumeric(record.usefulLife)) ? Formatters.int(record.usefulLife) : 0;
            record.remainingUsefulLife_sort = (AppUtilities.isNumeric(record.remainingUsefulLife)) ? Formatters.int(record.remainingUsefulLife) : 0;

            record.included = _.get(record, 'included', (record.avgCost_sort !== 0 && record.usefulLife !== 'N/A') ? true : false) as boolean;

            record.hypIncluded = _.get(record, 'hypIncluded', (record.avgCost_sort !== 0) ? true : false) as boolean;
            record.hypAvgCost = Formatters.float(_.get(record, 'hypAvgCost', record.avgCost_sort), 2);
            record.hypUsefulLife = Formatters.int(_.get(record, 'hypUsefulLife', record.usefulLife_sort));
            record.hypRemainingUsefulLife = Formatters.int(_.get(record, 'hypRemainingUsefulLife', record.remainingUsefulLife_sort));

            record.hypoIncluded = _.get(record, 'hypoIncluded', (record.avgCost_sort !== 0) ? true : false) as boolean;
            record.hypoAvgCost = Formatters.float(_.get(record, 'hypoAvgCost', record.avgCost_sort), 2);
            record.hypoUsefulLife = Formatters.int(_.get(record, 'hypoUsefulLife', record.usefulLife_sort));
            record.hypoRemainingUsefulLife = Formatters.int(_.get(record, 'hypoRemainingUsefulLife', record.remainingUsefulLife_sort));

            // Scenario inventory changes
            let invScenarioChanges = _.find(scenarioInventoryIn, { inventoryId: record.inventoryId });
            if (invScenarioChanges) {
                const invScenarioMerge = _.pick(invScenarioChanges, ['componentName', 'hypAvgCost', 'hypUsefulLife', 'hypRemainingUsefulLife', 'included']);
                if (enableTracing) { console.log(`${msgLogPrefix} inventoryId ${record.inventoryId} HAS scenarioInventoryChanges`, { invScenarioChanges, invScenarioMerge, record }); }

                record = _.merge(record, invScenarioMerge);
            }

            // Inventory changes
            let invChanges = _.find(inventoryChangesIn, { inventoryId: record.inventoryId });
            if (invChanges) {
                if (enableTracing) { console.log(`${msgLogPrefix} inventoryId ${record.inventoryId} HAS inventory changes`, { invChanges, record, inventoryChangesIn }); }
                record = _.merge(record, _.omit(invChanges, 'inventoryId'));
            }

            record.editing = record.included;

            if (enableTracing) { console.log(`${msgLogPrefix} inventoryId ${record.inventoryId}`, { invChanges, invScenarioChanges, scenarioInventoryIn, record, inventoryChangesIn }); }

            return record;
        });

        // if (this.originalExtendedInventoryCache.length === 0) { this.originalExtendedInventoryCache = _.cloneDeep(extended); }

        return extended;
    }

    // GETTERS
    public getFormData(assoc: any, currentOrder: any, currentOrderChart: any) { // , users: any) {
        const { visibleScenarios } = this.props;

        const enableTracing: boolean = false;

        const selectedScenarioObjId = _.get(this.state, 'formChanges.scenarioObjId', '');
        const scenario = (selectedScenarioObjId && _.size(visibleScenarios)) ? _.find(visibleScenarios, { '_id': selectedScenarioObjId }) : undefined;
        const scenarioUserObjId = _.get(scenario, 'user.relatedDocObjectId', '');

        if (enableTracing) { console.log(`portal.tsx::getFormData selectedScenarioObjId ${selectedScenarioObjId}`, { scenario }); }

        const auth = new Auth();
        const userObjId = auth.getSessionUserObjectId();

        const canUpdateScenario = (selectedScenarioObjId && userObjId && (String(scenarioUserObjId) === String(userObjId))) ? true : false;

        let myScenarios = _.stubArray();
        let sharedScenarios = _.stubArray();

        if (_.size(visibleScenarios)) {
            myScenarios = _.filter(visibleScenarios, (o) => String(_.get(o, 'user.relatedDocObjectId', '')) === String(userObjId));
            sharedScenarios = _.filter(visibleScenarios, (o) => String(_.get(o, 'user.relatedDocObjectId', '')) !== String(userObjId));
        }

        let result = ReportCommon.GetFormData(assoc, currentOrder, currentOrderChart);

        let specialAssessmentResult = 0;
        // if(_.size(currentOrderChart.specialAssessments) > 0 || _.size(currentOrderChart.specialAssessmentsCustomer) > 0) {
        if (_.size(currentOrderChart.specialAssessments) > 0) { // only consider our recommendations
            let specialAssessmentsAsStrings = _.map(currentOrderChart.specialAssessments[0], 'amount');
            // let specialAssessmentsCustomerAsStrings = _.map(currentOrderChart.specialAssessmentsCustomer[0], 'amount');
            let specialAssessments = _.map(specialAssessmentsAsStrings, _.toNumber);
            // let specialAssessmentsCustomer = _.map(specialAssessmentsCustomerAsStrings, _.toNumber);
            specialAssessmentResult = _.sum(specialAssessments);
            // specialAssessmentResult = _.sum(specialAssessments) + _.sum(specialAssessmentsCustomer);
        } else if (currentOrderChart.sa) {
            specialAssessmentResult = currentOrderChart.sa;
        }

        let resultHypo = {
            hypoBegBalance: Formatters.getFloat(currentOrderChart, 'begBalance', 2),
            hypoInterest: Formatters.getFloat(currentOrderChart, 'interest', 5, 1),
            hypoInflation: Formatters.getFloat(currentOrderChart, 'inflation', 5, 1),
            hypoAnnualIncrease: Formatters.getFloat(currentOrderChart, 'recommendedAnnualIncrease', 5, 1, 0.03),
            hypoMRCVal: Formatters.getFloat(currentOrderChart, 'recommendedMRCVal', 2),
            hypoSecondaryAnnualIncrease: Formatters.getFloat(currentOrderChart, 'recommendedSecondaryAnnualIncrease', 5, 1, 0.03),
            hypoYears: Formatters.getInt(currentOrderChart, 'recommendedYears', ReportCommon.CalculateYears),
            hypoSA: Formatters.float(specialAssessmentResult, 2),
        };

        let resultScenario = {
            scenarioObjId: selectedScenarioObjId,
            scenarioName: Formatters.getStr(scenario, 'name'),
            scenarioDescription: Formatters.getStr(scenario, 'description'),
            scenarioShared: (_.get(scenario, 'private', true)) ? false : true,
            scenarioCanUpdate: canUpdateScenario,
            myScenarios: myScenarios,
            sharedScenarios: sharedScenarios,
            scenarioHypoInventory: _.stubArray(), // 20200303 was: _.get(scenario, 'hypoInventory', []),
        };

        result = _.merge(result, resultHypo, resultScenario);

        // SCENARIO
        if (scenario) {
            /*
            result.begBalance = Formatters.getFloat(scenario, 'hypoBegBalance', 2);
            result.interest = Formatters.getFloat(scenario, 'hypoInterest', 5, 1);
            result.inflation = Formatters.getFloat(scenario, 'hypoInflation', 5, 1);
            */

            result.hypoBegBalance = Formatters.getFloat(scenario, 'hypoBegBalance', 2);
            result.hypoInterest = Formatters.getFloat(scenario, 'hypoInterest', 5, 1);
            result.hypoInflation = Formatters.getFloat(scenario, 'hypoInflation', 5, 1);
            result.hypoAnnualIncrease = Formatters.getFloat(scenario, 'hypoAnnualIncrease', 5, 1, 0.03);
            result.hypoMRCVal = Formatters.getFloat(scenario, 'hypoMRCVal', 2);
            result.hypoSecondaryAnnualIncrease = Formatters.getFloat(scenario, 'hypoSecondaryAnnualIncrease', 5, 1, 0.03);
            result.hypoYears = Formatters.getInt(scenario, 'hypoYears', ReportCommon.CalculateYears);
            result.hypoSA = Formatters.getFloat(scenario, 'hypoSA', 2);

            /*
            result.currentAnnualIncrease = Formatters.getFloat(scenario, 'hypoAnnualIncrease', 5, 1, 0.03);
            result.currentMRCVal = Formatters.getFloat(scenario, 'hypoMRCVal', 2);
            result.currentSecondaryAnnualIncrease = Formatters.getFloat(scenario, 'hypoSecondaryAnnualIncrease', 5, 1, 0.03);
            result.currentYears = Formatters.getInt(scenario, 'hypoYears', ReportCommon.CalculateYears);

            result.recommendedMRCVal = Formatters.getFloat(scenario, 'hypoMRCVal', 2); // 20200303 - Allows auto recalculation when changed
            result.recommendedAnnualIncrease = Formatters.getFloat(scenario, 'hypoAnnualIncrease', 5, 1, 0.03); // 20200303 - Allows auto recalculation when changed
            result.recommendedSecondaryAnnualIncrease = Formatters.getFloat(scenario, 'hypoSecondaryAnnualIncrease', 5, 1, 0.03); // 20200303 - Allows auto recalculation when changed
            result.recommendedYears = Formatters.getInt(scenario, 'hypoYears', ReportCommon.CalculateYears); // 20200303 - Allows auto recalculation when changed
            */

            result.calculateYears = Formatters.getInt(scenario, 'hypoYears', ReportCommon.CalculateYears);
            result.calculateYearsShort = Formatters.getInt(scenario, 'hypoYears', ReportCommon.CalculateYears) - 1;

            result.sa = Formatters.getFloat(scenario, 'hypoSA', 2);
            result.specialAssessment = Formatters.getFloat(scenario, 'hypoSA', 2);

            result.scenarioName = Formatters.getStr(scenario, 'name');
            result.scenarioDescription = Formatters.getStr(scenario, 'description');
            const shared = (_.get(scenario, 'private', true)) ? false : true;

            result.scenarioShared = shared;

            // Inventory
            result.scenarioHypoInventory = _.get(scenario, 'hypoInventory', []);
        }

        const res = _.merge({}, result, this.state.formChanges);

        // if (enableTracing) { console.log(`portal.tsx::getFormData merging`, { res, scenario, result, resultScenario, stateFormChanges: this.state.formChanges }); }

        return res;
    }

    public getIsHypothetical() {
        // 20200224 was: const isHypothetical = (this.state.inventoryChanges.length === 0 && _.keys(this.state.formChanges).length === 0) ? false : true;
        // 20200302 was: const isHypothetical = (_.size(_.keys(this.state.inventoryChanges)) === 0 && _.keys(this.state.formChanges).length === 0) ? false : true;
        const fcCount = this.getFormChangesCount();
        const isHypothetical = (fcCount === 0) ? false : true;

        // console.log(`portal.tsx::getIsHypothetical fcCount ${fcCount} isHypothetical ${isHypothetical}`);

        return isHypothetical;
    }

    public getCurrentOrderChartClone(currentOrderChart: any, formData: any) {
        return _.merge({}, currentOrderChart, _.pick(formData, [
            'begBalance',
            'beginYear',
            'calculateYears',
            'currentAnnualIncrease',
            'currentMRCVal',
            'currentSecondaryAnnualIncrease',
            'currentYears',
            'draft',
            'groupComponents',
            'groupComponentsPictureSection',
            'hideComponentEvaluation',
            'hideFooter',
            'inflation',
            'interest',
            'hypoBegBalance', // 20200303 added
            'hypoInterest', // 20200303 added
            'hypoInflation', // 20200303 added
            'hypoAnnualIncrease', // 20200303 added
            'hypoMRCVal', // 20200303 added
            'hypoSA', // 20200303 added
            'hypoSecondaryAnnualIncrease', // 20200303 added
            'hypoYears', // 20200303 added
            'minimumAnnualIncrease',
            'minimumMRCVal',
            'minimumSecondaryAnnualIncrease',
            'minimumYears',
            'perFirstYear',
            'recommendedAnnualIncrease',
            'recommendedMRCVal',
            'recommendedSecondaryAnnualIncrease',
            'recommendedYears',
            'sa',
            'specialAssessments',
            'specialAssessmentsCustomer',
            'showCurrent',
            'showPageNumbers',
            'showMinimum',
            'showRecommended',
            'versionId']));
    }

    public getFormChangesCount() {
        // 20200302 was: return _.size(this.state.formChanges);
        const numFormChanges = _.size(_.keys(_.get(this.state, 'formChanges', {})));
        const numInvChanges = _.size(_.keys(_.get(this.state, 'inventoryChanges', {})));

        const res = numFormChanges + numInvChanges;

        // console.log(`portal.tsx::getFormChangesCount numFormChanges ${numFormChanges} numInvChanges ${numInvChanges} res ${res}`);

        return res;
    }

    public getScenarioLabel(formData: any) {
        const scenarioLabel = _.get(formData, 'scenarioName', ' ');
        return scenarioLabel;
    }

    // HANDLERS
    // public handleAdjustmentFormInputChange(n: any, v: any) {
    //     let changes = Object.assign({}, this.state.formChanges);
    //     changes[n] = Number(v);

    //     this.setState({ formChanges: changes });
    // }

    // public handleAdjustmentFormSubmit(e: any) {
    //     console.log('handleAdjustmentFormSubmit', e);
    // }

    public handleItemChange(event: any) {
        const itemField = event.field;
        const itemValueNew = event.value;
        const itemValueOld = event.dataItem[itemField];
        const itemId = event.dataItem.inventoryId;
        const origItem = _.find(this.originalExtendedInventoryCache, { 'inventoryId': itemId });
        const origValue = _.get(origItem, itemField);

        const enableTracing: boolean = false;
        const logMsgPrefix: string = `report/portal.tsx handleItemChange itemId ${itemId}`;

        if (enableTracing) { console.log(`${logMsgPrefix} called: field ${itemField} change from ${itemValueOld} to ${itemValueNew}. orig ${origValue}`, origItem); }

        let inv = _.cloneDeep(_.get(this.state, 'inventoryChanges', {}));
        if (!_.find(inv, { 'inventoryId': itemId })) {
            const newItem = { inventoryId: itemId, };
            // 20200224 was: inv.push(newItem);

            if (enableTracing) { console.log(`${logMsgPrefix} not found in inv, extending...`, { newItem, inv }); }
            _.set(inv, itemId, newItem);
        }

        let item = _.find(inv, { 'inventoryId': itemId });
        if (item && _.get(item, itemField) !== itemValueNew) {
            if (itemValueNew === origValue) {
                // New value same as original, delete any change
                if (enableTracing) { console.log(`${logMsgPrefix} New value same as original, delete any change. unset item itemField`); }
                _.unset(item, itemField);
            } else {
                // New value differs from original
                if (enableTracing) { console.log(`${logMsgPrefix} New value differs from original, set change. set item itemField`); }
                _.set(item, itemField, itemValueNew);
            }

            if (_.keys(_.omit(item, 'inventoryId')).length === 0) {
                // No more changes for this item, remove it
                if (enableTracing) { console.log(`${logMsgPrefix} No more changes for this item, remove it`); }

                _.unset(inv, itemId); // let invRejected = _.reject(inv, { inventoryId: itemId });
                // inv = (_.size(_.reject(inv, { inventoryId: itemId });
            }

            // _.set(event.dataItem, itemField, itemValueNew);

            if (enableTracing) { console.log(`${logMsgPrefix} succeeded handle request to change field ${itemField} from ${itemValueOld} to ${itemValueNew}. orig ${origValue}`, { origItem, item, inv }); }

            this.setState({ inventoryChanges: inv });
        } /* else {
            console.log(`${logMsgPrefix} failed handle request to change field ${itemField} from ${itemValueOld} to ${itemValueNew}. orig ${origValue}`, { origItem, item, inv });
        } */
    }

    public handleInviteUserButton(e: any) {
        const { associationId } = this.props;
        this.props.navigateTo(NavigationLinks.inviteAdd(associationId));
    }

    public handleToggleInvite(portalInvite: any) {
        this.setState({ showModalToggleInvite: true, toggleInvite: _.cloneDeep(portalInvite) });
    }

    public handleShowModalChangeInvite() {
        // console.log(`handleShowModalChangeInvite`);
        this.setState({ showModalToggleInvite: true });
    }

    public handleCancelAndCloseModalChangeInvite() {
        // console.log(`handleCancelAndCloseModalChangeInvite`);
        this.setState({ showModalToggleInvite: false, toggleInvite: {} });
    }

    public handleResendInvite(portalInvite: any, action: any) {
        // if (!portalInvite || !_.includes([PortalAccessActions.Enable, PortalAccessActions.Disable], action)) { return; }
        if (!portalInvite) { return; }

        const token = _.get(this.props, 'authsession.access_token', '');

        const changeData = {
            resendClientInvite: portalInvite,
            action: action,
        };

        this.props.resendClientInvite(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                // this.originalFormDataCache = {};

                // this.setState({ formChanges: {}, showModalCreateClientInvite: false });

                this.setState({ showModalToggleInvite: false, toggleInvite: {} });
            }
        });

    }

    public handleConfirmModalChangeInvite(portalInvite: any, action: any) {
        if (!portalInvite || !_.includes([PortalAccessActions.Enable, PortalAccessActions.Disable, PortalAccessActions.Modify], action)) { return; }

        const token = _.get(this.props, 'authsession.access_token', '');

        const changeData = {
            toggleClientInvite: portalInvite,
            action: action,
        };

        this.props.toggleClientInvite(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                // this.originalFormDataCache = {};

                // this.setState({ formChanges: {}, showModalCreateClientInvite: false });

                this.setState({ showModalToggleInvite: false, toggleInvite: {} });
            }
        });
    }

    public handleAdjustmentFormInputChange(e: any, x: any) {
        const startFormChange = new Date().getTime();

        let fieldName = '';
        let valueNew;

        const enableTracing: boolean = false;
        const logMsgPrefix: string = `report/portal.tsx handleAdjustmentFormInputChange fieldName ${fieldName}`;

        if (_.get(e, 'target.props.name')) {
            // Kendo form element

            fieldName = _.get(e, 'target.props.name', '');
            valueNew = _.get(e, 'value');
        } else {
            const eventTargetType = _.get(e, 'target.type', '');

            switch (eventTargetType) {
                case 'textarea':
                case 'text':
                case 'number':
                    fieldName = _.get(e, 'target.name', '');
                    valueNew = _.get(e, 'target.value');
                    break;

                case 'virtualized-select':
                    fieldName = _.get(e, 'target.name', '');
                    valueNew = _.get(x, 'value');
                    break;

                default:
                    break;
            }
        }

        if (!fieldName) { return; }

        let formChanges = _.cloneDeep(this.state.formChanges);

        let applyChange = false;
        let origVal = _.get(this.originalFormDataCache, fieldName);
        const isDifferent = (String(valueNew) !== String(origVal)) ? true : false;

        if (isDifferent) {
            // New value
            _.set(formChanges, fieldName, valueNew);
            applyChange = true;
        } else {
            // Same as orig val
            if (_.has(formChanges, fieldName)) {
                _.unset(formChanges, fieldName);
                applyChange = true;
            }
        }

        if (enableTracing) { console.log(`${logMsgPrefix} change from origVal ${origVal} to ${valueNew} isDifferent ${isDifferent} applyChange ${applyChange}`, { e, formChanges }); }

        const startFormChangeSetState = new Date().getTime();
        if (applyChange) {
            this.setState((prevState) => ({ formChanges: formChanges }), () => {
                const endFormChange = new Date().getTime();
                // console.log(`handleFormInputChange total time ${endFormChange - startFormChange}ms setState time ${endFormChange - startFormChangeSetState}ms`);
            });
        }
    }

    public handleFormSubmit(e: any) {
        // console.log('handleFormSubmit', e);
    }

    public handleSaveButton(e: any, viewPdfAfterSave: boolean = false) {
        const token = _.get(this.props, 'authsession.access_token', '');
        // console.log('handleSaveButton formChanges', this.state.formChanges);

        const { associationId, association, currentOrder, currentOrderChart } = this.props;
        const assoc = this.extendAssociationData(association);
        const formData = this.getFormData(assoc, currentOrder, currentOrderChart);

        const changeData = _.merge(
            {},
            { currentOrder: _.pick(this.props.currentOrder, ['orderId', '_id']) },
            { formData: formData },
            this.state.formChanges
        );

        // console.log('passing changeData', changeData);

        return;
    }

    public handleUpdateScenarioButton(e: any) {
        const token = _.get(this.props, 'authsession.access_token', '');
        // console.log('handleUpdateScenarioButton formChanges', this.state.formChanges);

        const auth = new Auth();
        const userObjId = auth.getSessionUserObjectId();

        const { associationId, association, currentOrder, currentOrderChart } = this.props;

        const stateFormChanges = _.get(this.state, 'formChanges', {});
        const stateInventoryChanges = _.get(this.state, 'inventoryChanges', {});
        const cacheOrigInventory = _.map(this.originalExtendedInventoryCache, (o) => {
            return _.pick(o, [
                '_id',
                'hypAvgCost',
                'hypIncluded',
                'included', // 20200303 added
                'hypRemainingUsefulLife',
                'hypUsefulLife',
                'hypoAvgCost',
                'hypoIncluded',
                'hypoRemainingUsefulLife',
                'hypoUsefulLife',
                'inventoryId',
                'componentName', // 20210930
            ]);
        });

        const reportScenarioObjId = Formatters.getStr(stateFormChanges, 'scenarioObjId');

        const changeData = _.merge(
            {},
            {
                reportScenario: {
                    userObjId: userObjId,
                    associationObjId: _.get(association, '_id', ''),
                    reserveStudyChartObjId: _.get(currentOrderChart, '_id', ''),
                    reportScenarioObjId: reportScenarioObjId,
                },
            },
            {
                reportScenario: _.pick(stateFormChanges, [
                    'begBalance',
                    'interest',
                    'inflation',
                    'currentAnnualIncrease',
                    'currentMRCVal',
                    'hypoBegBalance', // 20200303 added
                    'hypoInterest', // 20200303 added
                    'hypoInflation', // 20200303 added
                    'hypoAnnualIncrease', // 20200303 added
                    'hypoMRCVal', // 20200303 added
                    'hypoSecondaryAnnualIncrease', // 20200303 added
                    'hypoYears', // 20200303 added
                    'hypoSA',
                    'recommendedMRCVal', // 20200303 added
                    'recommendedAnnualIncrease', // 20200303 added
                    'recommendedSecondaryAnnualIncrease', // 20200303 added
                    'recommendedYears', // 20200303 added
                    'currentSecondaryAnnualIncrease',
                    'calculateYears',
                    'currentYears',
                    'sa',
                    'specialAssessments',
                    'specialAssessmentsCustomer',
                    'scenarioName',
                    'scenarioDescription',
                    'scenarioShared'
                ])
            },
            { reportScenarioInventory: stateInventoryChanges, },
            { reportScenarioInventoryOrig: cacheOrigInventory },
        );

        // console.log(`portal.tsx::handleUpdateScenarioButton POSTing changeData`, { changeData, stateFormChanges, stateInventoryChanges });

        this.props.updateReportScenario(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                // history.push(NavigationLinks.associationDetail(response.body.associationId));
                // FORCE RELOAD
                this.originalFormDataCache = {};
                this.originalExtendedInventoryCache = [];
                this.setState(s => ({
                    formChanges: {
                        scenarioObjId: reportScenarioObjId
                    },
                    inventoryChanges: {},
                    showModalSaveChanges: false,
                    gridKey: new Date().getTime().toString(),
                }));
            }
        });
    }
    // copy / new scenario
    public handleCopyScenarioButton(e: any) {
        const token = _.get(this.props, 'authsession.access_token', '');

        const auth = new Auth();
        const userObjId = auth.getSessionUserObjectId();

        const { association, currentOrderChart } = this.props;

        const stateFormChanges = _.get(this.state, 'formChanges', {});
        const stateInventoryChanges = _.get(this.state, 'inventoryChanges', {});
        const cacheOrigInventory = _.map(this.originalExtendedInventoryCache, (o) => {
            return _.pick(o, [
                '_id',
                'hypAvgCost',
                'hypIncluded',
                'included', // 20200303 added
                'hypRemainingUsefulLife',
                'hypUsefulLife',
                'hypoAvgCost',
                'hypoIncluded',
                'hypoRemainingUsefulLife',
                'hypoUsefulLife',
                'inventoryId',
            ]);
        });
        const changeData = _.merge(
            {},
            {
                reportScenario: {
                    userObjId: userObjId,
                    associationObjId: _.get(association, '_id', ''),
                    reserveStudyChartObjId: _.get(currentOrderChart, '_id', ''),
                    reportScenarioObjId: _.get(stateFormChanges, 'scenarioObjId', ''),
                },
            },
            {
                reportScenario: _.pick(stateFormChanges, [
                    'begBalance',
                    'interest',
                    'inflation',
                    'currentAnnualIncrease',
                    'currentMRCVal',
                    'recommendedMRCVal', // 20200303 added
                    'recommendedAnnualIncrease', // 20200303 added
                    'recommendedSecondaryAnnualIncrease', // 20200303 added
                    'recommendedYears', // 20200303 added
                    'hypoBegBalance', // 20200303 added
                    'hypoInterest', // 20200303 added
                    'hypoInflation', // 20200303 added
                    'hypoAnnualIncrease', // 20200303 added
                    'hypoMRCVal', // 20200303 added
                    'hypoSecondaryAnnualIncrease', // 20200303 added
                    'hypoYears', // 20200303 added
                    'hypoSA',
                    'currentSecondaryAnnualIncrease',
                    'calculateYears',
                    'currentYears',
                    'sa',
                    'specialAssessments',
                    'specialAssessmentsCustomer',
                    'scenarioName',
                    'scenarioDescription',
                    'scenarioShared'
                ])
            },
            { reportScenarioInventory: stateInventoryChanges },
            { reportScenarioInventoryOrig: cacheOrigInventory },
        );

        // console.log(`portal.tsx::handleCopyScenarioButton POSTing changeData`, { changeData, stateFormChanges, stateInventoryChanges, cacheOrigInventory });

        this.props.copyReportScenario(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                // history.push(NavigationLinks.associationDetail(response.body.associationId));
                // FORCE RELOAD
                this.originalFormDataCache = {};
                this.originalExtendedInventoryCache = [];
                const newScenarioObjId = _.get(response, 'body.reportScenario.document._id', '');
                // console.log(`handleCopyScenarioButton scenarioObjId to ${newScenarioObjId}`);
                this.setState(s => ({
                    formChanges: {
                        scenarioObjId: newScenarioObjId
                    },
                    inventoryChanges: {},
                    showModalSaveChanges: false,
                    gridKey: new Date().getTime().toString(),
                }));
            }
        });
    }

    public handleDeleteScenarioButton(e: any) {
        const token = _.get(this.props, 'authsession.access_token', '');

        const auth = new Auth();
        const userObjId = auth.getSessionUserObjectId();

        const { associationId, association, currentOrder, currentOrderChart } = this.props;

        const changeData = _.merge(
            {},
            {
                reportScenario: {
                    userObjId: userObjId,
                    associationObjId: _.get(association, '_id', ''),
                    reserveStudyChartObjId: _.get(currentOrderChart, '_id', ''),
                    reportScenarioObjId: _.get(this.state, 'formChanges.scenarioObjId', ''),
                },
            },
        );

        // console.log('passing changeData to handleDeleteScenarioButton', {changeData});

        this.props.deleteReportScenario(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                this.originalFormDataCache = {};
                this.originalExtendedInventoryCache = [];
                // const showPanelScenarios = (this.panelScenariosDefaultExpanded) ? true : false;
                // console.log(`handleDeleteScenarioButton deleted reportScenarioObjId ${changeData.reportScenario.reportScenarioObjId}`);
                this.setState(s => ({
                    formChanges: {},
                    inventoryChanges: {},
                    showModalSaveChanges: false,
                    gridKey: new Date().getTime().toString(),
                }));
            }
        });
    }

    public mrcFromExpenditureData(recordSet: any) {
        const invMap = AppUtilities.getArray(recordSet, 'invMap');
        let expenditureTotal = 0;
        invMap.forEach(e => expenditureTotal += e.expenditureMap[0].signAsDollar);
        return Math.floor(expenditureTotal / 12);
    }

    public findBestYearByMrcVal(cloneCurrentOrderChart: any, cloneCurrentOrder: any, inventoryIncluded: any, years: any) {
        const cloneCurrentOrderChartLocked = cloneCurrentOrderChart;
        const calculationResult = PortalReportLogic.computeFundingCalculations(cloneCurrentOrderChartLocked, cloneCurrentOrder, inventoryIncluded, years);
        const calculations = AppUtilities.getArray(calculationResult, 'recordSet');
        const wellFunded = (e) => Math.abs(e.hypoPctFunded - 100) <= 1.75;
        let wellFundedYears = calculations.filter(wellFunded);

        if (wellFundedYears.length === 0) {
            return 0;
        }

        let firstYearTo100 = calculations.findIndex(wellFunded);
        return firstYearTo100;
    }

    public firstPassFundingCheck(initialFundedness: number) {
        // const calculations = preRunCalc;
        const fundednessCheck = initialFundedness;
        // const fundednessCheck = calculations[0].hypoPctFunded;
        if (fundednessCheck < 100) {
            return 'under';
        }
        if (fundednessCheck > 102.9) {
            return 'over';
        }
        return 'well';
    }

    public findFndSlpCombo(cloneCurrentOrderChart: any, cloneCurrentOrder: any, inventoryIncluded: any, years: any) {
        const calculationResult = PortalReportLogic.computeFundingCalculations(cloneCurrentOrderChart, cloneCurrentOrder, inventoryIncluded, years);
        const calculations = AppUtilities.getArray(calculationResult, 'recordSet');
        const pctRet = (
            Math.abs(calculations[26].hypoPctFunded) +
            Math.abs(calculations[27].hypoPctFunded - 1) +
            Math.abs(calculations[28].hypoPctFunded - 1.5) +
            Math.abs(calculations[29].hypoPctFunded - 2)
        ) / 4.0; // well funded would result in 100

        let fundingDelta = (
            Math.abs(calculations[26].hypoPctFunded - 100) +
            Math.abs(calculations[27].hypoPctFunded - 100) +
            Math.abs(calculations[28].hypoPctFunded - 100) +
            Math.abs(calculations[29].hypoPctFunded - 100)
        ) / 4.0; // well funded would result in 0

        if ((pctRet - 100) < 0) {
            fundingDelta = fundingDelta * -1;
        }

        const slopeRet = (
            Math.abs(calculations[26].hypoPctFunded - calculations[25].hypoPctFunded) +
            Math.abs(calculations[27].hypoPctFunded - calculations[26].hypoPctFunded) +
            Math.abs(calculations[28].hypoPctFunded - calculations[27].hypoPctFunded) +
            Math.abs(calculations[29].hypoPctFunded - calculations[28].hypoPctFunded)
        ) / 4.0; // well funded would result in 0

        // let comboRet = (slopeRet * 0.25) + (fundingDelta * 1.25) + (pctRet);
        let comboRet = (slopeRet + fundingDelta + pctRet);
        return comboRet;
    }

    public findSlp(cloneCurrentOrderChart: any, cloneCurrentOrder: any, inventoryIncluded: any, years: any) {
        const calculationResult = PortalReportLogic.computeFundingCalculations(cloneCurrentOrderChart, cloneCurrentOrder, inventoryIncluded, years);
        const calculations = AppUtilities.getArray(calculationResult, 'recordSet');
        const rise = calculations[29].hypoPctFunded - calculations[26].hypoPctFunded;
        const run = 4;
        const slope = rise / run;

        return slope;
    }

    public customMinimizer(formData: any) {// (initialValues: any, formData: any) {
        let stateInvChanges = _.get(this.state, 'inventoryChanges', {});
        const { association, currentOrder, currentOrderChart } = this.props;
        const assoc = this.extendAssociationData(association);
        const scenarioInventory = _.get(formData, 'scenarioHypoInventory', []);
        const inventory: Array<any> = this.extendInventoryData(this.props.inventory, stateInvChanges, assoc, scenarioInventory);
        const inventoryIncluded = _.cloneDeep(_.filter(inventory, e => Formatters.getBoolFalse(e, 'included')));
        const iterations: number = 49;
        let safeIterations: number = 0;
        const exitEarly: number = 50;
        let cloneCurrentOrderChart = this.getCurrentOrderChartClone(_.cloneDeep(currentOrderChart), formData);
        let cloneCurrentOrder = _.cloneDeep(currentOrder);

        let optimizerSkipCombo = this.findFndSlpCombo(cloneCurrentOrderChart, cloneCurrentOrder, inventoryIncluded, ReportCommon.CalculateYears);
        let optimizerSkip = (optimizerSkipCombo < 96 || optimizerSkipCombo > 104) ? 'not well' : 'well';

        cloneCurrentOrderChart.years = ReportCommon.CalculateYears;
        let initialValues = [cloneCurrentOrderChart.hypoMRCVal, cloneCurrentOrderChart.hypoYears,
        cloneCurrentOrderChart.hypoSecondaryAnnualIncrease, cloneCurrentOrderChart.hypoAnnualIncrease,
        cloneCurrentOrderChart.hypoInterest, cloneCurrentOrderChart.hypoInflation];
        let preRun = PortalReportLogic.computeFundingCalculations(cloneCurrentOrderChart, cloneCurrentOrder, inventoryIncluded, ReportCommon.CalculateYears);
        let yearToUse = 0;
        let workingVals = initialValues; // [MRC, year, secondaryAnnualIncrease, annualIncrease, interest, inflation]
        let adjustableMRC = this.mrcFromExpenditureData(preRun);
        let adjustableAnnualIncrease = cloneCurrentOrderChart.hypoInflation; //  + 0.0025;
        let adjustableSecAnnInc = adjustableAnnualIncrease;
        workingVals[0] = adjustableMRC;
        workingVals[3] = adjustableAnnualIncrease;
        workingVals[2] = adjustableSecAnnInc;

        cloneCurrentOrderChart.hypoMRCVal = workingVals[0];
        cloneCurrentOrderChart.hypoYears = workingVals[1];
        cloneCurrentOrderChart.hypoSecondaryAnnualIncrease = workingVals[2];
        cloneCurrentOrderChart.hypoAnnualIncrease = workingVals[3];
        cloneCurrentOrderChart.hypoInterest = workingVals[4];
        cloneCurrentOrderChart.hypoInflation = workingVals[5];

        let fundedness = this.firstPassFundingCheck(this.findFndSlpCombo(cloneCurrentOrderChart, cloneCurrentOrder, inventoryIncluded, ReportCommon.CalculateYears));

        let canBeOptimized = (optimizerSkip === 'well') ? false : true;
        let skipSecondPass = false;

        // first pass section //
        while (fundedness !== 'well' && canBeOptimized) { // something wrong with funding
            if (safeIterations > exitEarly) {
                canBeOptimized = false;
                break;
            }
            fundedness = this.firstPassFundingCheck(this.findFndSlpCombo(cloneCurrentOrderChart, cloneCurrentOrder, inventoryIncluded, ReportCommon.CalculateYears));
            if (fundedness === 'over') { // funding too high
                adjustableMRC = adjustableMRC * 0.99;
                workingVals[0] = adjustableMRC;
                cloneCurrentOrderChart.hypoMRCVal = adjustableMRC;
                cloneCurrentOrderChart.hypoYears = workingVals[1];
                cloneCurrentOrderChart.hypoSecondaryAnnualIncrease = workingVals[2];
                let yearCheck = this.findBestYearByMrcVal(cloneCurrentOrderChart, cloneCurrentOrder, inventoryIncluded, ReportCommon.CalculateYears);
                if (yearCheck > 0) { // yearCheck will be the index of the first year at proper funding or 0 for invalid results
                    yearToUse = (yearCheck >= 12) ? yearCheck - 7 : yearCheck; // set year (Rob suggests the first year at proper funding minus 8, we use -7 as yearCheck is already -1 from being an index.)
                    workingVals[0] = adjustableMRC * (1 + (workingVals[5] / 2)); // set year and MRC based on Rob's methodology
                    workingVals[1] = yearToUse; // set year and MRC based on Rob's methodology
                    fundedness = this.firstPassFundingCheck(this.findFndSlpCombo(cloneCurrentOrderChart, cloneCurrentOrder, inventoryIncluded, ReportCommon.CalculateYears));
                    // break;
                }
            }
            if (fundedness === 'under') { // funding too low
                skipSecondPass = false;
                for (let i = 0; i < iterations; i++) { // needs to increase
                    adjustableMRC = adjustableMRC * 1.01;
                    workingVals[0] = adjustableMRC;
                    cloneCurrentOrderChart.hypoMRCVal = adjustableMRC;
                    cloneCurrentOrderChart.hypoYears = workingVals[1];
                    cloneCurrentOrderChart.hypoSecondaryAnnualIncrease = workingVals[2];
                    let yearCheck = this.findBestYearByMrcVal(cloneCurrentOrderChart, cloneCurrentOrder, inventoryIncluded, ReportCommon.CalculateYears);
                    if (yearCheck > 0) { // yearCheck will be the index of the first year at proper funding or 0 for invalid results
                        // do we need to use a different year?
                        const existingYearRun = this.findFndSlpCombo(cloneCurrentOrderChart, cloneCurrentOrder, inventoryIncluded, ReportCommon.CalculateYears);
                        let modifiedCCOC = _.cloneDeep(cloneCurrentOrderChart);
                        modifiedCCOC.hypoYears = yearCheck - 7;
                        modifiedCCOC.hypoSecondaryAnnualIncrease = modifiedCCOC.hypoAnnualIncrease + 0.005;
                        const modifiedYearRun = this.findFndSlpCombo(modifiedCCOC, cloneCurrentOrder, inventoryIncluded, ReportCommon.CalculateYears);
                        if ((Math.abs(existingYearRun - 100) > Math.abs(modifiedYearRun - 100))) {
                            yearToUse = (yearCheck >= 14) ? yearCheck - 7 : yearCheck; // set year (Rob suggests the first year at proper funding minus 8, we use -7 as yearCheck is already -1 from being an index.)
                            workingVals[0] = adjustableMRC * (1 + (workingVals[5] / 2)); // set year and MRC based on Rob's methodology
                            workingVals[1] = yearToUse; // set year and MRC based on Rob's methodology
                            fundedness = 'well';
                            break;
                        } else {
                            yearToUse = (yearCheck >= 8) ? yearCheck - 4 : yearCheck; // workingVals[1];
                            skipSecondPass = true;
                            workingVals[0] = adjustableMRC * (1 + (workingVals[5] / 2)); // set year and MRC based on Rob's methodology
                            workingVals[1] = yearToUse; // set year and MRC based on Rob's methodology
                            fundedness = 'well';
                            break;
                        }
                    }
                }
            }

            safeIterations++;
        }

        // second pass section //
        if (canBeOptimized) {
            let secondPassUnresolved = (skipSecondPass) ? false : true;
            adjustableSecAnnInc = workingVals[2];
            cloneCurrentOrderChart.hypoMRCVal = workingVals[0];
            cloneCurrentOrderChart.hypoYears = workingVals[1];
            cloneCurrentOrderChart.hypoSecondaryAnnualIncrease = adjustableSecAnnInc;
            let spSafeIterations = 0;
            let spExitEarly = 50;
            let spYearAdjustments = 16;
            let combo = this.findFndSlpCombo(cloneCurrentOrderChart, cloneCurrentOrder, inventoryIncluded, ReportCommon.CalculateYears);
            if (combo < 99 || combo > 103) {
                secondPassUnresolved = true;
            }
            while (secondPassUnresolved) {
                if (spSafeIterations > spExitEarly) {
                    if (spYearAdjustments > 0) {
                        if (workingVals[1] < 2) {
                            workingVals[1] = 30;
                        } else {
                            spYearAdjustments--;
                            workingVals[1] = workingVals[1] - 2;
                        }
                        cloneCurrentOrderChart.hypoYears = workingVals[1];
                        combo = this.findFndSlpCombo(cloneCurrentOrderChart, cloneCurrentOrder, inventoryIncluded, ReportCommon.CalculateYears);
                        spSafeIterations = 0;
                    } else {
                        canBeOptimized = false;
                        break;
                    }
                }
                if (combo >= 99 && combo < 103) {
                    secondPassUnresolved = false;
                    adjustableSecAnnInc = (Math.floor(adjustableSecAnnInc * 100000) / 100000);
                    workingVals[2] = adjustableSecAnnInc;
                    cloneCurrentOrderChart.hypoSecondaryAnnualIncrease = adjustableSecAnnInc;
                    cloneCurrentOrderChart.hypoMRCVal = workingVals[0];
                    cloneCurrentOrderChart.hypoYears = workingVals[1];
                } else {
                    if (combo < 99) {
                        adjustableSecAnnInc += 0.00025;
                        cloneCurrentOrderChart.hypoSecondaryAnnualIncrease = adjustableSecAnnInc;
                        combo = this.findFndSlpCombo(cloneCurrentOrderChart, cloneCurrentOrder, inventoryIncluded, ReportCommon.CalculateYears);
                    } else {
                        adjustableSecAnnInc -= 0.00025;
                        cloneCurrentOrderChart.hypoSecondaryAnnualIncrease = adjustableSecAnnInc;
                        combo = this.findFndSlpCombo(cloneCurrentOrderChart, cloneCurrentOrder, inventoryIncluded, ReportCommon.CalculateYears);
                    }
                }
                spSafeIterations++;

            }
        }
        let result = {
            workingVals: workingVals,
            canBeOptimized: canBeOptimized,
            alreadyOptimized: (optimizerSkip === 'well') ? true : false
        };
        return result;
    }

    public handleOptimizeScenario(formData: any, selectedScenarioObjId?: any) {
        let solution = this.customMinimizer(formData); // (initialValues, formData);
        let hypoBegBalance = formData.hypoBegBalance;
        if (solution.canBeOptimized === false && solution.alreadyOptimized === false) {
            this.setState({ showModalOptimizeScenario: true, showModalAlreadyOptimized: false });
        } else if (solution.alreadyOptimized === true) {
            this.setState({ showModalAlreadyOptimized: true, showModalOptimizeScenario: false });
        } else {
            // workvals[0] = MRC, workvals[1] = years, workvals[2] = secondary annual increase, workvals[3] = annual increase, workvals[4] = interest, workvals[5] = inflation
            let fcToUse = (selectedScenarioObjId) ? {
                'hypoMRCVal': Math.floor(_.get(solution.workingVals, [0])),
                'hypoYears': solution.workingVals[1],
                'hypoAnnualIncrease': solution.workingVals[3],
                'hypoSecondaryAnnualIncrease': solution.workingVals[2],
                'hypoBegBalance': hypoBegBalance,
                'hypoInterest': solution.workingVals[4],
                'hypoInflation': solution.workingVals[5],
                'scenarioObjId': selectedScenarioObjId
            } : {
                'hypoMRCVal': Math.floor(_.get(solution.workingVals, [0])),
                'hypoYears': solution.workingVals[1],
                'hypoSecondaryAnnualIncrease': solution.workingVals[2],
                'hypoBegBalance': hypoBegBalance,
                'hypoInterest': solution.workingVals[4],
                'hypoInflation': solution.workingVals[5],
                'hypoAnnualIncrease': solution.workingVals[3]
            };
            let mergedFormChanges = _.merge({}, this.state.formChanges, fcToUse);
            this.setState({ formChanges: mergedFormChanges });
        }
        this.setState({ modalOptimizeBusy: false });
    }

    public handleOptimizeButton(e: any) {
        const { association, currentOrder, currentOrderChart } = this.props;
        const assoc = this.extendAssociationData(association);
        const formData = this.getFormData(assoc, currentOrder, currentOrderChart);
        this.handleOptimizeScenario(formData, _.get(formData, 'scenarioObjId'));
    }

    public handleCancelAndCloseModalOptimizeScenario() {
        this.setState({ showModalOptimizeScenario: false });
    }

    public handleCancelAndCloseModalAlreadyOptimized() {
        this.setState({ showModalAlreadyOptimized: false });
    }

    public handleChartTabsSaveButton(e: any) {
        // console.log(`portal.tsx::handleChartTabsSaveButton`, { e });
        this.setState({ showPanelScenarios: true });
    }

    public handleToggleScenarioPanel(expanded: boolean = false) {
        this.setState({ showPanelScenarios: expanded });
    }

    public handleRevertScenarioButton(e: any) {
        const showPanelScenarios = (this.panelScenariosDefaultExpanded) ? true : false;

        this.originalFormDataCache = {};
        this.originalExtendedInventoryCache = []; // 20200302

        this.setState(s => ({
            formChanges: {},
            showPanelScenarios: showPanelScenarios,
            showModalSaveChanges: false,
            inventoryChanges: {}, // 20200302 added to reset grid input values
            gridKey: new Date().getTime().toString(),
        }));
    }

    public handleNewScenarioResetButton(e: any) {
        this.originalFormDataCache = {};
        this.originalExtendedInventoryCache = []; // 20200302

        this.setState(s => ({
            formChanges: {},
            showModalSaveChanges: false,
            inventoryChanges: {}, // 20200302 added to reset grid input values
            gridKey: new Date().getTime().toString(),
        }));
    }

    public handleDropChangesButton(e: any, formData: any) {
        this.handleScenarioChange(_.get(formData, 'scenarioObjId'));
    }

    // RENDERERS
    public renderPortalAccessPanelHeaderButton() {
        let buttonProps = {
            bsSize: 'xs',
            bsStyle: 'info',
        };

        return (
            <ButtonGroup>
                <Button {...buttonProps} onClick={e => { this.handleInviteUserButton(e); }}>
                    <i className="rs-icon rs-icon-user-plus" /> {this.appText('app.portal-invites.invite-user', false)}
                </Button>
            </ButtonGroup>
        );
    }

    public renderConfirmChangePortalInvite() {
        const toggleInvite = _.get(this.state, 'toggleInvite');
        const user = _.get(toggleInvite, 'user');
        const association = _.get(toggleInvite, 'association');

        let inviteAction = PortalAccessActions.Disable;
        let accessLevelAction = PortalAccessActions.Modify;
        let actionName = this.appText('modals.toggle-invite.disable');
        let modalTitle = this.appText('modals.toggle-invite.modify');
        let modalBody = (<div />);

        if (toggleInvite && user && association) {
            // determine if we're enabling/deactivating portal access
            const isValid = (UserUtilities.IsPortalInviteValid(toggleInvite)) ? true : false;
            const isPortalAdmin = (UserUtilities.GetRoleText(Formatters.getStr(toggleInvite, 'roleName')) === PortalRoles.PortalAdmin.RoleText);

            if (!isValid) {
                inviteAction = PortalAccessActions.Enable;
                actionName = this.appText('modals.toggle-invite.allow');
            }

            const alternateInputProps = { colLabelXs: 6, colInputXs: 6 };

            let inputPropsHalfLargeTextDebounced = _.merge({}, alternateInputProps, {
                handleChange: (n, v) => { return; },
                className: 'inventory-detail-input-half-width',
            });

            const expireDate = (inviteAction === PortalAccessActions.Enable) ? moment().add('months', 6).toDate() : _.get(toggleInvite, 'roleExpirationDate');
            const expiresOn = (expireDate) ? moment(expireDate).format(AppConfiguration.getMomentShortDateFormat()) : '';
            const expireDateValue = (expiresOn) ? expiresOn : this.appText('modals.toggle-invite.deactivate-never');

            let modalAccessLevelChangeButtonProps = {
                bsStyle: 'warning',
                bsSize: 'sm',
                marginTop: '20',
                title: (isPortalAdmin) ? (this.appText('modals.toggle-invite.demote')) : (this.appText('modals.toggle-invite.promote')),
            };

            let modalToggleActivateInviteButtonProps = {
                bsStyle: 'warning',
                bsSize: 'sm',
                title: (isValid) ? (this.appText('modals.toggle-invite.disable-access')) : (this.appText('modals.toggle-invite.enable-access')),
            };

            let modalResendInviteButtonProps = {
                bsStyle: 'info',
                bsSize: 'sm',
                title: this.appText('modals.toggle-invite.resend'),
            };

            const buttonAccessLevelChange = (isValid) ? (
                <Button {...modalAccessLevelChangeButtonProps} onClick={(e) => { this.handleConfirmModalChangeInvite(toggleInvite, accessLevelAction); }}>
                    {(isPortalAdmin) ? (<i className="fa fa-arrow-down" />) : (<i className="fa fa-arrow-up" />)} {modalAccessLevelChangeButtonProps.title}
                </Button>) : (<span />);

            const buttonToggleActivateInvite =
                <Button {...modalToggleActivateInviteButtonProps} onClick={(e) => { this.handleConfirmModalChangeInvite(toggleInvite, inviteAction); }}>
                    <i className="fa fa-warning" /> {modalToggleActivateInviteButtonProps.title}
                </Button>;

            const buttonResendInvite =
                <Button {...modalResendInviteButtonProps} onClick={(e) => { this.handleResendInvite(toggleInvite, inviteAction); }}>
                    <i className="fa fa-share" /> {modalResendInviteButtonProps.title}
                </Button>;

            modalBody = (
                <div>
                    <div className="modal-portal-access-toggle-user">
                        <Row>
                            <Col xs={8} >
                                <KendoWrappers.KendoDisabledInputHidden
                                    name="_modal.user"
                                    label={this.appText('modals.toggle-invite.user')}
                                    value={Formatters.composeUsernameLabel(user, true)}
                                    {...inputPropsHalfLargeTextDebounced}
                                />

                                <KendoWrappers.KendoDisabledInputHidden
                                    name="_modal.association"
                                    label={this.appText('modals.toggle-invite.association')}
                                    value={Formatters.composeAssociationNameLabel(association, true)}
                                    {...inputPropsHalfLargeTextDebounced}
                                />

                                <KendoWrappers.KendoDisabledInputHidden
                                    name="_modal.role"
                                    label={this.appText('modals.toggle-invite.access-level')}
                                    value={UserUtilities.GetRoleText(Formatters.getStr(toggleInvite, 'roleName'))}
                                    {...inputPropsHalfLargeTextDebounced}
                                />

                                <KendoWrappers.KendoDisabledInputHidden
                                    name="_modal.expire"
                                    label={this.appText('modals.toggle-invite.deactivate-on')}
                                    value={expireDateValue}
                                    {...inputPropsHalfLargeTextDebounced}
                                />
                            </Col>
                            <Col xs={4}>
                                <div className="modal-portal-button-wrapper">
                                    {buttonResendInvite}</div>
                                <div className="modal-portal-button-wrapper">
                                    {buttonToggleActivateInvite}</div>
                                <div className="modal-portal-button-wrapper">
                                    {buttonAccessLevelChange}</div>
                            </Col>
                        </Row>
                    </div>
                </div>
            );
        }

        return (
            <Modal show={this.state.showModalToggleInvite} onHide={() => this.handleCancelAndCloseModalChangeInvite()}>
                <Modal.Header closeButton={true}>
                    <Modal.Title>{modalTitle} {this.appText('modals.toggle-invite.title')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {modalBody}
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={(e) => { this.handleCancelAndCloseModalChangeInvite(); }}>{this.appText('app.common.buttons.cancel', false)}</Button>
                </Modal.Footer>
            </Modal>
        );
    }

    public renderOptimizeFailureModal() {
        const alternateInputProps = { colLabelXs: 6, colInputXs: 6 };

        let inputPropsHalfLargeTextDebounced = _.merge({}, alternateInputProps, {
            handleChange: (n, v) => { return; },
            className: 'inventory-detail-input-half-width',
        });
        let modalBody = (<div />);
        modalBody = (
            <div>
                <div className="modal-portal-access-toggle-user">
                    <Row>
                        <Col xs={8} >
                            <KendoWrappers.KendoDisabledInputHidden
                                name="_modal.explanation"
                                label={'Warning: '}
                                value={this.appText('app.reserve-study.optimize-failure-explanation', false)}
                                {...inputPropsHalfLargeTextDebounced}
                            />
                        </Col>
                    </Row>
                </div>
            </div>
        );

        let fullModal = (<div />);
        fullModal = (<div>
            <Modal.Header closeButton={true}>
                <Modal.Title>{'Unable to Optimize Scenario'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {modalBody}
            </Modal.Body>
            <Modal.Footer>
                <ButtonGroup>
                    <Checkbox onClick={(e) => {
                        (this.state.modalOptimizeDismiss) ? this.setState({ modalOptimizeDismiss: false }) :
                            this.setState({ modalOptimizeDismiss: true });
                    }}>{'Do not show this dialog again.'}</Checkbox>
                    <Button onClick={(e) => { this.handleCancelAndCloseModalOptimizeScenario(); }}>{this.appText('app.common.buttons.close', false)}</Button>
                </ButtonGroup>
            </Modal.Footer>
        </div>);

        return (
            <Modal show={this.state.showModalOptimizeScenario && !this.state.modalOptimizeDismiss} onHide={() => { this.handleCancelAndCloseModalOptimizeScenario(); }}>
                {fullModal}
            </Modal>
        );
    }

    public renderAlreadyOptimizedModal() {
        const alternateInputProps = { colLabelXs: 6, colInputXs: 6 };

        let inputPropsHalfLargeTextDebounced = _.merge({}, alternateInputProps, {
            handleChange: (n, v) => { return; },
            className: 'inventory-detail-input-half-width',
        });
        let modalBody = (<div />);
        modalBody = (
            <div>
                <div className="modal-portal-access-toggle-user">
                    <Row>
                        <Col xs={8} >
                            <KendoWrappers.KendoDisabledInputHidden
                                name="_modal.explanation"
                                label={'Warning: '}
                                value={this.appText('app.reserve-study.already-optimized-explanation', false)}
                                {...inputPropsHalfLargeTextDebounced}
                            />
                        </Col>
                    </Row>
                </div>
            </div>
        );

        let fullModal = (<div />);
        fullModal = (<div>
            <Modal.Header closeButton={true}>
                <Modal.Title>{'Unable to Optimize Scenario'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {modalBody}
            </Modal.Body>
            <Modal.Footer>
                <ButtonGroup>
                    <Button onClick={(e) => { this.handleCancelAndCloseModalAlreadyOptimized(); }}>{this.appText('app.common.buttons.close', false)}</Button>
                </ButtonGroup>
            </Modal.Footer>
        </div>);

        return (
            <Modal show={this.state.showModalAlreadyOptimized} onHide={() => { this.handleCancelAndCloseModalAlreadyOptimized(); }}>
                {fullModal}
            </Modal>
        );
    }

    public handleScenarioChange(newScenarioObjId: any) {
        this.originalFormDataCache = {};
        this.originalExtendedInventoryCache = [];
        this.setState(s => ({
            formChanges: {
                scenarioObjId: newScenarioObjId,
            },
            inventoryChanges: {}, // 20200303 ADDED
            showModalSaveChanges: false,
            gridKey: new Date().getTime().toString(),
        }));
    }

    public renderPanelScenarios(formData: any, isExpanded: boolean = true, currentSelectedScenarioObjId: any) {
        const buildScenarioSnapshots = (buildScenarios: any, allowScenarioChanges: boolean = false, appendAuthor: boolean = false, appendShared: boolean = false) => {

            let res = (<div />);

            const colProps = {
                xs: 12,
                sm: 12,
                md: 12,
                lg: 12,
                xl: 12,
            };

            if (_.size(buildScenarios)) {
                res = (
                    <div>
                        {
                            _.map(buildScenarios, (s) => (
                                <Col {...colProps} key={`hdl-report-scenario-col-${_.get(s, '_id', '')}`}>
                                    {UserUtilities.BuildScenarioTiledButtonAction(
                                        s,
                                        allowScenarioChanges,
                                        (e) => { this.handleScenarioChange(Formatters.getStr(e, '_id')); },
                                        appendAuthor,
                                        appendShared,
                                        this.appText('my-scenario-shared'),
                                        this.appText('my-scenario-not-shared'),
                                    )}
                                </Col>
                            ))
                        }
                    </div>
                );
            }

            return res;
        };

        const { myScenarios, sharedScenarios } = formData;

        const myPrivateScenarios = _.filter(myScenarios, (o) => Formatters.getStr(o, 'private') === 'true');
        const mySharedScenarios = _.filter(myScenarios, (o) => Formatters.getStr(o, 'private') !== 'true');

        const isScenarioLoaded = (_.get(formData, 'scenarioObjId')) ? true : false;
        const canUpdateScenario = (_.get(formData, 'scenarioCanUpdate', false)) ? true : false;
        const canCopyScenario = true;
        const canDeleteScenario = (_.get(formData, 'scenarioCanUpdate', false)) ? true : false;
        const panelExpanded = (isExpanded) ? true : false;

        let noScenarioLoadedText = this.appText('no-scenario');
        let loadedScenarioName = Formatters.getStr(_.find(_.get(formData, 'myScenarios'), ['_id', _.get(formData, 'scenarioObjId')]), 'name');

        let adjustableScenarioName = (_.get(formData, 'scenarioName'));
        let renameAwareTitle = (!isScenarioLoaded) ?
            noScenarioLoadedText :
            ((loadedScenarioName === adjustableScenarioName) ?
                'Scenario name: ' + adjustableScenarioName :
                'Name change \'' + loadedScenarioName + '\' to: ' + adjustableScenarioName);

        const defaultInputProps = {
            colLabelXs: 4,
            colInputXs: 8,
        };

        let inputProps = _.merge({}, defaultInputProps, {
            onChange: (n, v) => {
                this.handleAdjustmentFormInputChange(n, v);
            },
        });

        // copy/new button
        let copyScenarioButtonProps = {
            bsStyle: 'success',
            bsSize: 'sm',
            disabled: (Formatters.getStr(formData, 'scenarioName')) ? false : true,
            title: (isScenarioLoaded) ? this.appText('save-copy') : this.appText('save-new'),
        };

        // save a copy
        let saveAsCopyScenarioButtonProps = {
            bsStyle: 'success',
            bsSize: 'sm',
            title: this.appText('save-copy'),
        };

        // start a new scenario reset button
        let newScenarioResetButtonProps = {
            bsStyle: 'warning',
            bsSize: 'sm',
            title: this.appText('scenario-new-reset'),
        };

        // update/save button
        let updateScenarioButtonProps = {
            bsStyle: 'success',
            bsSize: 'sm',
            title: this.appText('update-scenario'),
        };

        // delete button
        let deleteScenarioButtonProps = {
            bsStyle: 'danger',
            bsSize: 'sm',
            title: this.appText('delete-scenario'),
        };

        // delete button
        let dropChangesButtonProps = {
            bsStyle: 'warning',
            bsSize: 'sm',
            title: this.appText('drop-changes'),
        };

        // optimize button
        let scenarioPanelOptimizeButtonProps = {
            bsStyle: 'success',
            bsSize: 'sm',
            title: this.appText('optimize-scenario'),
        };

        // rename button
        let renameScenarioButtonProps = {
            bsStyle: 'info',
            bsSize: 'sm',
            title: this.appText('rename-scenario'),
        };

        let inputPropsTextDebounced = _.merge({}, defaultInputProps, {
            handleChange: (n, v) => { this.handleAdjustmentFormInputChange(n, v); },
        });

        let scenarioHeader = this.appText('save-current-scenario');

        // defaulted to showing both columns even when no scenarios are saved or shared.
        const colMySavedScenarios = (_.size(myScenarios)) ? (
            <div className="panel-scenario-section panel-scenario-section-third">
                <div>
                    <h5 className="panel-scenario-section-header"><b>{this.appText('my-scenarios')}&nbsp;</b></h5>
                    <div className="panel-portal-scenarios-container">
                        <Row>
                            {buildScenarioSnapshots(myPrivateScenarios, true, false, false)}
                        </Row>
                    </div>
                </div>
            </div>
        ) : (<div className="panel-scenario-section panel-scenario-section-third">
            <div>
                <h5 className="panel-scenario-section-header"><b>{this.appText('my-scenarios')}&nbsp;</b></h5>
                <div className="panel-portal-scenarios-container" />
            </div>
        </div>);

        const colMySharedScenarios = (_.size(myScenarios)) ? (
            <div className="panel-scenario-section panel-scenario-section-third">
                <div>
                    <h5 className="panel-scenario-section-header"><b>{this.appText('shared-by-me')}&nbsp;</b></h5>
                    <div className="panel-portal-scenarios-container">
                        <Row>
                            {buildScenarioSnapshots(mySharedScenarios, true, false, false)}
                        </Row>
                    </div>
                </div>
            </div>
        ) : (<div className="panel-scenario-section panel-scenario-section-third">
            <div>
                <h5 className="panel-scenario-section-header"><b>{this.appText('shared-by-me')}&nbsp;</b></h5>
                <div className="panel-portal-scenarios-container" />
            </div>
        </div>);

        const colSharedByOthersScenarios = (_.size(sharedScenarios)) ? (
            <div className="panel-scenario-section panel-scenario-section-third">
                <div>
                    <h5 className="panel-scenario-section-header"><b>{this.appText('shared-by-others')}&nbsp;</b></h5>
                    <div className="panel-portal-scenarios-container">
                        <Row>
                            {buildScenarioSnapshots(sharedScenarios, false, true, false)}
                        </Row>
                    </div>
                </div>
            </div>
        ) : (<div className="panel-scenario-section panel-scenario-section-third">
            <div>
                <h5 className="panel-scenario-section-header"><b>{this.appText('shared-by-others')}&nbsp;</b></h5>
                <div className="panel-portal-scenarios-container" />
            </div>
        </div>);

        const buttonDeleteScenario = (canDeleteScenario) ? (
            <Button {...deleteScenarioButtonProps} onClick={(e) => { this.handleDeleteScenarioButton(e); }}>
                <i className="fa fa-trash-o" /> {deleteScenarioButtonProps.title}
            </Button>) : (<span />);

        const buttonUpdate = (canUpdateScenario) ? (
            <Button {...updateScenarioButtonProps} onClick={(e) => { this.handleUpdateScenarioButton(e); }}>
                <i className="fa fa-floppy-o" /> {updateScenarioButtonProps.title}
            </Button>) : (<span />);

        const buttonCopy = (canCopyScenario && !canUpdateScenario) ? (
            <Button {...copyScenarioButtonProps} onClick={(e) => { this.handleCopyScenarioButton(e); }}>
                <i className="fa fa-files-o" /> {copyScenarioButtonProps.title}
            </Button>) : (<span />);

        const buttonSaveAsCopy = (canCopyScenario && canUpdateScenario) ? (
            <Button {...saveAsCopyScenarioButtonProps} onClick={(e) => { this.handleCopyScenarioButton(e); }}>
                <i className="fa fa-files-o" /> {saveAsCopyScenarioButtonProps.title}
            </Button>) : (<span />);

        const buttonNewScenarioReset = (canCopyScenario && canUpdateScenario) ? (
            <Button {...newScenarioResetButtonProps} onClick={(e) => { this.handleNewScenarioResetButton(e); }}>
                <i className="fa fa-undo" /> {newScenarioResetButtonProps.title}
            </Button>) : (<span />);

        const buttonDropChanges = (canCopyScenario && canUpdateScenario) ? (
            <Button {...dropChangesButtonProps} onClick={(e) => { this.handleDropChangesButton(e, formData); }}>
                <i className="fa fa-undo" /> {dropChangesButtonProps.title}
            </Button>) : (<span />);

        const buttonScenarioPanelOptimize = (canCopyScenario && canUpdateScenario) ? (
            <Button {...scenarioPanelOptimizeButtonProps} onClick={(e) => { this.setState({ modalOptimizeBusy: true }, () => this.handleOptimizeScenario(formData, _.get(formData, 'scenarioObjId'))); }}>
                <i className="fa fa-check" /> {scenarioPanelOptimizeButtonProps.title}
            </Button>) : (<span />);

        /*         const buttonRenameScenario = (canCopyScenario && canUpdateScenario) ? (
                    <Button {...renameScenarioButtonProps} onClick={(e) => { this.handleRenameScenarioButton(e); }}>
                        <i className="fa fa-pencil" /> {renameScenarioButtonProps.title}
                    </Button>) : (<span />); */

        return (
            <Row>
                <Col xs={12}>
                    <Panel id="portal-report-scenarios-panel" className="portal-report-portal-access-panel" expanded={panelExpanded} onToggle={(e) => this.handleToggleScenarioPanel(e)}>
                        <Panel.Heading className="clearfix">
                            <Panel.Title toggle={true}>
                                <div className="portal-report-portal-access-panel-heading">
                                    <i className="fa fa-bar-chart portal-report-portal-access-panel-heading-icon" /> {this.appText('scenario-snapshots')}
                                </div>
                            </Panel.Title>
                        </Panel.Heading>
                        <Panel.Collapse>
                            <Panel.Body>
                                <div>
                                    <WhiteBoxPageSection pageSectionColXl={12} pageSectionColMd={12} title="">
                                        <Row>
                                            <Col xs={12} lg={6} xl={7}>
                                                {colMySavedScenarios}
                                                {colMySharedScenarios}
                                                {colSharedByOthersScenarios}
                                            </Col>

                                            <Col xs={12} lg={5} lgOffset={1} xl={4} xlOffset={1}>
                                                <div className="panel-scenario-section">
                                                    <div>
                                                        <h5 className="panel-scenario-section-header"><b>{scenarioHeader}&nbsp;</b></h5>
                                                        <div>
                                                            <PortalReserveStudyFormSection
                                                                title={renameAwareTitle}>
                                                                <KendoWrappers.KendoTextInputDebounced
                                                                    name="scenarioName"
                                                                    label={this.appText('scenario-name')}
                                                                    // disabled={(Formatters.getStr(formData, 'scenarioName') ? true : false)}
                                                                    value={Formatters.getStr(formData, 'scenarioName')}
                                                                    required={true}
                                                                    {...inputPropsTextDebounced}
                                                                />

                                                                {/* (buttonRenameScenario) */}

                                                                <KendoWrappers.KendoTextareaDebounced
                                                                    name="scenarioDescription"
                                                                    label={this.appText('scenario-description')}
                                                                    value={Formatters.getStr(formData, 'scenarioDescription')}
                                                                    required={false}
                                                                    rows={3}
                                                                    {...inputPropsTextDebounced}
                                                                />

                                                                <KendoWrappers.KendoCheckbox
                                                                    name="scenarioShared"
                                                                    label={this.appText('scenario-shared')}
                                                                    value={_.get(formData, 'scenarioShared', false)}
                                                                    className="portal-scenario-shared-checkbox"
                                                                    {...inputProps}
                                                                />
                                                                <ButtonToolbar>
                                                                    {(buttonDeleteScenario)}
                                                                </ButtonToolbar>
                                                                <hr />
                                                                <ButtonToolbar>
                                                                    <ButtonGroup>
                                                                        {/*(buttonScenarioPanelOptimize)*/}
                                                                        {(buttonUpdate)}
                                                                        {(buttonCopy)}
                                                                        {(buttonSaveAsCopy)}
                                                                        {(buttonDropChanges)}
                                                                        {(buttonNewScenarioReset)}
                                                                    </ButtonGroup>
                                                                </ButtonToolbar>
                                                            </PortalReserveStudyFormSection>
                                                        </div>
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>
                                    </WhiteBoxPageSection>
                                </div>
                            </Panel.Body>
                        </Panel.Collapse>
                    </Panel>
                </Col>
            </Row>
        );
    }

    public render() {
        console.log('BG: this.state', this.state);
        const { currentOrder, currentOrderChart, portalInvites, association, associationId } = this.props;

        const assoc = this.extendAssociationData(association);

        const pageTitle = this.appTextPageTitleAppendString(Formatters.getStr(association, 'name'), 'app.report-portal-association.page-title', false);
        let page = (<div />);

        const isLoading = Number(Formatters.getInt(association, 'associationId')) !== Number(associationId);
        const noChartData = _.isEmpty(currentOrder) ||
            _.isEmpty(currentOrderChart) ||
            _.isEmpty(association) ||
            _.isEmpty(this.props.inventory);

        if (!isLoading && noChartData) {
            page = (
                <div>
                    <Row>
                        <Col xs={4}>
                            <Alert bsStyle="info">
                                <h4>{this.appText('chart-data-missing')}</h4>
                                <p>{this.appText('chart-data-missing-body')}</p>
                            </Alert>
                        </Col>
                    </Row>
                </div>
            );
        }

        if (!noChartData && !isLoading) {
            const startRender = new Date().getTime();
            const enableTracing: boolean = false;

            const isHypothetical = this.getIsHypothetical(); // (this.getFormChangesCount() > 0) ? true : false;
            let showHypothetical = this.getIsHypothetical(); // (this.getFormChangesCount() > 0) ? true : false;
            const formData = this.getFormData(assoc, currentOrder, currentOrderChart);

            const isScenarioLoaded = (_.get(formData, 'scenarioObjId')) ? true : false;
            const canUpdateScenario = (_.get(formData, 'scenarioCanUpdate', false)) ? true : false;
            // const canSeeScenarioInventory = (!isScenarioLoaded || (_.get(formData, 'scenarioCanUpdate', false))) ? true : false; // disabled on Sept-15-2021, TO-DO: rather than disable display of inventory make inventory non-editable
            const canSeeScenarioInventory = true;
            let currentSelectedScenarioObjId = (_.get(formData, 'scenarioObj')) ? _.get(formData, 'scenarioObj') : undefined;

            let stateInvChanges = _.get(this.state, 'inventoryChanges', {});

            const scenarioInventory = _.get(formData, 'scenarioHypoInventory', []);
            const inventory: Array<any> = this.extendInventoryData(this.props.inventory, stateInvChanges, assoc, scenarioInventory);

            let cloneCurrentOrderChart = this.getCurrentOrderChartClone(_.cloneDeep(currentOrderChart), formData);
            let cloneCurrentOrder = _.cloneDeep(currentOrder);

            cloneCurrentOrderChart.years = ReportCommon.CalculateYears; // cloneCurrentOrderChart.calculateYears;

            // currentOrder
            cloneCurrentOrder = _.merge({}, cloneCurrentOrder, _.pick(formData, [
                'coverPagePicture',
                'customDisclosureTitle',
                'customDisclosureText',
                'startDate']));

            if (enableTracing) { console.log(`portal.tsx::render gridKey ${Formatters.getStr(this.state, 'gridKey')}`, { cloneCurrentOrderChart, cloneCurrentOrder, stateInvChanges, scenarioInventory, inventory, formData }); }

            const inventoryIncluded = _.cloneDeep(_.filter(inventory, e => Formatters.getBoolFalse(e, 'included'))); // 20200303 was: _.get(e, 'included', false)));
            const calculationResult = PortalReportLogic.computeFundingCalculations(cloneCurrentOrderChart, cloneCurrentOrder, inventoryIncluded, ReportCommon.CalculateYears);
            const calculations = AppUtilities.getArray(calculationResult, 'recordSet');
            const calculationsCorrected = _.take(calculations, ReportCommon.CalculateYears);

            const invMap = AppUtilities.getArray(calculationResult, 'invMap');
            const percentFunded = PortalReportLogic.getPercentFunded(calculationsCorrected, isHypothetical, false);
            const fundedAmount = PortalReportLogic.getFundedAmount(calculationsCorrected, isHypothetical, false);
            const fullyFunded = PortalReportLogic.getFullyFunded(calculationsCorrected, isHypothetical, false);

            const yearlySummaryData = ReportCharts.getYearlySummaryGridData((e) => this.appText(e), calculationsCorrected, formData.beginYear, ReportCommon.CalculateYears, isHypothetical);
            const yearSummary = _.find(yearlySummaryData, ['year', formData.beginYear]);

            const startingBalance = _.toNumber(_.get(currentOrder, 'startingBalance', 0));
            const begBalance = _.toNumber(_.get(currentOrderChart, 'begBalance', startingBalance));
            const idealBalance = _.toNumber(_.get(yearSummary, (isHypothetical) ? 'h_ffb' : 'ffb', begBalance));
            const percentageFunded = percentFunded > 0 ? percentFunded / 100 : 0;

            formData.percentFunded = Formatters.float(percentFunded, 2);
            formData.percentageFunded = (percentageFunded > 0) ? Formatters.float(percentageFunded, 4) : 0;
            formData.currentFFB = idealBalance;

            if (formData.numberUnits > 0) {
                formData.perUnitLiability = (formData.currentFFB > 0) ? Formatters.float((formData.currentFFB / formData.numberUnits), 2) : formData.perUnitLiability;

                formData.currentMrcPerUnit = (formData.currentMRCVal > 0) ? Formatters.float((formData.currentMRCVal / formData.numberUnits), 2) : formData.currentMrcPerUnit;
                formData.minimumMrcPerUnit = (formData.minimumMRCVal > 0) ? Formatters.float((formData.minimumMRCVal / formData.numberUnits), 2) : formData.minimumMrcPerUnit;
                formData.recommendedMrcPerUnit = (formData.recommendedMRCVal > 0) ? Formatters.float((formData.recommendedMRCVal / formData.numberUnits), 2) : formData.recommendedMrcPerUnit;

                formData.minimumMrcPerUnitIncrease = (formData.minimumMrcPerUnit > 0) ? Formatters.float((formData.minimumMrcPerUnit - formData.currentMrcPerUnit), 2, 1) : formData.minimumMrcPerUnitIncrease;
                formData.recommendedMrcPerUnitIncrease = (formData.recommendedMrcPerUnit > 0) ? Formatters.float((formData.recommendedMrcPerUnit - formData.currentMrcPerUnit), 2, 1) : formData.recommendedMrcPerUnitIncrease;

                formData.minimumMrcPerUnitIncreasePercent = (formData.currentMRCVal > 0 && formData.minimumMrcPerUnit > 0) ?
                    Formatters.float((formData.currentMrcPerUnit / formData.minimumMrcPerUnit), 2, 1) :
                    formData.minimumMrcPerUnitIncreasePercent;

                formData.recommendedMrcPerUnitIncreasePercent = (formData.currentMRCVal > 0 && formData.recommendedMrcPerUnit > 0) ?
                    Formatters.float((formData.currentMrcPerUnit / formData.recommendedMrcPerUnit), 2, 1) :
                    formData.recommendedMrcPerUnitIncreasePercent;
            }

            if (!_.size(this.originalFormDataCache)) {
                if (enableTracing) {
                    console.log(`portal.tsx::render RESET originalFormDataCache len ${_.size(this.originalFormDataCache)}`, {
                        thisOrigFormDataCache: this.originalFormDataCache
                    });
                }

                this.originalFormDataCache = _.cloneDeep(formData);
            }

            if (!_.size(this.originalExtendedInventoryCache)) {
                if (enableTracing) {
                    console.log(`portal.tsx::render RESET originalExtendedInventoryCache len ${_.size(this.originalExtendedInventoryCache)}`, {
                        thisOrigExtendedInventoryCache: this.originalExtendedInventoryCache
                    });
                }

                this.originalExtendedInventoryCache = _.cloneDeep(inventory);
            }

            // auth
            const auth = new Auth();
            const isStaff = (auth.isAdmin() || auth.isEmployee()) ? true : false;
            const isClient = auth.isClient();
            const userObjId = auth.getSessionUserObjectId();

            let panelManageUsersBody = (<div />);
            let portalAccessSectionVisible = (isStaff) ? true : false;

            let sectionManageUserAccess = (<div />);
            let sectionScenarios = (<div />);

            if (!isStaff && isClient && _.size(portalInvites)) {
                // Does current user have client roles and PortalAdmin role for this assoc?
                let isClientPortalAdmin = false;
                let activePortalInvites: any = UserUtilities.GetValidPortalInvites(portalInvites);
                if (_.size(activePortalInvites)) {
                    const filterAdminUser = UserUtilities.FilterPortalInvites(activePortalInvites, PortalRoles.PortalAdmin.RoleName);
                    isClientPortalAdmin = (_.find(filterAdminUser, (o) => _.get(o, 'user.userObjectId', '') === userObjId)) ? true : false;
                    // console.log(`isClientPortalAdmin ${isClientPortalAdmin} filterAdminUser:`, filterAdminUser);
                }
                portalAccessSectionVisible = (isClient && isClientPortalAdmin) ? true : false;
                // console.log(`isStaff ${isStaff} isClient ${isClient} isClientPortalAdmin ${isClientPortalAdmin} canViewPortalInvites ${canViewPortalInvites} activePortalInvites:`, activePortalInvites);
            }

            if (portalAccessSectionVisible) {
                const buildPortalManagers = (pi: any) => {
                    const validPortalInvites: any = UserUtilities.GetValidPortalInvites(pi);

                    const filterAdminUser = UserUtilities.FilterPortalInvites(validPortalInvites, PortalRoles.PortalAdmin.RoleName);
                    let usersAdmin = _.map(filterAdminUser, (o) => {
                        const canToggle = (isStaff || _.get(o, 'user.userObjectId', '') === userObjId) ? true : false;
                        return UserUtilities.BuildInviteButtonAction(o, false, !canToggle, (e) => { this.handleToggleInvite(e); });
                    });

                    if (!_.size(usersAdmin)) {
                        usersAdmin = [UserUtilities.BuildEmptyPortalInvites(associationId, PortalRoles.PortalAdmin.RoleName, this.appText('portal-access.none-assigned'))];
                    }

                    return usersAdmin;
                };

                const buildPortalAllowedUsers = (pi: any) => {
                    const validPortalInvites: any = UserUtilities.GetValidPortalInvites(pi);

                    const filterAllowedUser = UserUtilities.FilterPortalInvites(validPortalInvites, PortalRoles.PortalUser.RoleName);
                    let usersAllowed = _.map(filterAllowedUser, (o) => {
                        return UserUtilities.BuildInviteButtonAction(o, false, false, (e) => { this.handleToggleInvite(e); });
                    });

                    if (!_.size(usersAllowed)) {
                        usersAllowed = [UserUtilities.BuildEmptyPortalInvites(associationId, PortalRoles.PortalUser.RoleName, this.appText('portal-access.none-assigned'))];
                    }

                    return usersAllowed;
                };

                const buildPortalDeactivatedUsers = (pi: any) => {
                    const invalidPortalInvites: any = UserUtilities.GetInvalidPortalInvites(pi);

                    let usersDeactive = _.map(invalidPortalInvites, (o) => {
                        let res = (
                            <div key={`div-portalinvite-${_.get(o, 'association.associationObjectId', '')}-${_.get(o, 'user.userObjectId', '')}`}>
                                {UserUtilities.BuildInviteButtonAction(o, true, false, (e) => { this.handleToggleInvite(e); })}
                            </div>
                        );
                        return res;
                    });

                    if (!_.size(usersDeactive)) {
                        usersDeactive = [UserUtilities.BuildEmptyPortalInvites(associationId, 'deactivated', this.appText('portal-access.none-deactivated'))];
                    }

                    return usersDeactive;
                };

                panelManageUsersBody = (
                    <div>
                        <WhiteBoxPageSection pageSectionColXl={12} pageSectionColMd={12} title="">
                            <Row>
                                <Col xs={12} md={4}>
                                    <Row className="panel-access-section">
                                        <Col xs={12}>
                                            <h5 className="panel-access-section-header"><b>{this.appText('portal-access.managers')}&nbsp;</b></h5>
                                            {buildPortalManagers(portalInvites)}
                                        </Col>
                                    </Row>
                                </Col>

                                <Col xs={12} md={4}>
                                    <Row className="panel-access-section">
                                        <Col xs={12}>
                                            <h5 className="panel-access-section-header"><b>{this.appText('portal-access.allowed-users')}&nbsp;</b></h5>
                                            {buildPortalAllowedUsers(portalInvites)}
                                        </Col>
                                    </Row>
                                </Col>

                                <Col xs={12} md={4}>
                                    <Row className="panel-access-section">
                                        <Col xs={12}>
                                            <h5 className="panel-access-section-header"><b>{this.appText('portal-access.deactivated-users')}&nbsp;</b></h5>
                                            {buildPortalDeactivatedUsers(portalInvites)}
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                        </WhiteBoxPageSection>
                    </div>
                );

                sectionManageUserAccess = (
                    <Row>
                        <Col xs={12}>
                            {this.renderConfirmChangePortalInvite()}
                            <Panel
                                id="portal-report-portal-access-panel"
                                className="portal-report-portal-access-panel"
                                defaultExpanded={this.panelManageUsersDefaultExpanded}
                            >
                                <Panel.Heading className="clearfix">
                                    <Panel.Title toggle={true}>
                                        <div className="pull-right portal-report-portal-access-panel-button-group">
                                            {this.renderPortalAccessPanelHeaderButton()}
                                        </div>
                                        <div className="portal-report-portal-access-panel-heading">
                                            <i className="fa fa-users portal-report-portal-access-panel-heading-icon" /> {this.appText('app.portal-invites.manage-user-access', false)}
                                        </div>
                                    </Panel.Title>
                                </Panel.Heading>
                                <Panel.Collapse>
                                    <Panel.Body>
                                        {panelManageUsersBody}
                                    </Panel.Body>
                                </Panel.Collapse>
                            </Panel>
                        </Col>
                    </Row>
                );
            }

            const showPanelScenarios = (_.get(this.state, 'showPanelScenarios', this.panelScenariosDefaultExpanded)) ? true : false;
            sectionScenarios = this.renderPanelScenarios(formData, showPanelScenarios, currentSelectedScenarioObjId);

            let sectionInventoryGrids = (<div />);
            if (canSeeScenarioInventory) {
                sectionInventoryGrids = (
                    <div className="report-inventory-grids">
                        <ReportInventoryGrids.InventoryGridsSection
                            appTextGridColumnHeader={(e) => this.appTextGridColumnHeader(e)}
                            gridTitlePrefix=""
                            sectionTitle={this.appText('inventory-analyzed')}
                            inventory={inventory}
                            inventorySubgroups={_.get(association, 'inventorySubgroups', [])}
                            onInventoryItemChange={(e) => { this.handleItemChange(e); }}
                            gridKey={Formatters.getStr(this.state, 'gridKey')}
                        />
                    </div>
                );
            }

            page = (
                <div>
                    <Row>
                        <Col xs={12}>
                            {this.renderOptimizeFailureModal()}
                            {this.renderAlreadyOptimizedModal()}
                            {sectionManageUserAccess}
                            {sectionScenarios}
                            <WhiteBoxPageSection title={this.appText('report-parameters')}>
                                <Row>
                                    {/* Left column form */}
                                    <Col xs={12} lg={4} xl={3}>
                                        <PortalChartForm.PortalReserveStudyForm
                                            appText={(e) => this.appText(e)}
                                            handleFormInputChange={(n, v) => this.handleAdjustmentFormInputChange(n, v)}
                                            handleSubmitForm={(e) => this.handleFormSubmit(e)}
                                            handleViewFundingDisclosure={(e) => { return; }}
                                            handleEditOrderButton={(e, o) => { return; }}
                                            formData={formData}
                                            className="____form-inline"
                                            currentOrder={cloneCurrentOrder}
                                            currentOrderChart={cloneCurrentOrderChart}
                                            assoc={assoc}
                                            numFormChanges={this.getFormChangesCount()}
                                            handleSaveChanges={(e) => this.handleSaveButton(e)}
                                            handleCopyScenarioButton={(e) => this.handleCopyScenarioButton(e)}
                                            handleOptimizeScenarioButton={(e) => this.handleOptimizeButton(e)}
                                            handleUpdateScenarioButton={(e) => this.handleCopyScenarioButton(e)}
                                            handleViewPdf={(e) => { return; }}
                                        />

                                        <PercentFundedDisplay.PercentFundedSection
                                            appText={(e) => this.appText(e)}
                                            percentFunded={percentFunded}
                                            isHypothetical={isHypothetical}
                                            fullyFunded={fullyFunded}
                                            fundingAmount={fundedAmount}
                                        />
                                    </Col>

                                    {/* Right column charts */}
                                    <Col xs={12} lg={8} xl={9} className="portal-report-charts">
                                        <ReportCharts.ChartTabs
                                            calculations={calculationsCorrected}
                                            inventoryMap={invMap}
                                            tabsId="chartTabs"
                                            assoc={assoc}
                                            appText={(e) => this.appText(e)}
                                            isHypothetical={isHypothetical}
                                            onlyHypothetical={(showHypothetical) ? true : false}
                                            currentOrder={cloneCurrentOrder}
                                            currentOrderChart={cloneCurrentOrderChart}
                                            percentFunded={percentFunded}
                                            yearlySummaryData={yearlySummaryData}
                                            beginYear={formData.beginYear}
                                            yearsCount={ReportCommon.CalculateYears}
                                            appTextGridColumnHeader={(e) => this.appTextGridColumnHeader(e)}
                                            formData={formData}
                                            showButtons={true}
                                            disableButtons={!canUpdateScenario}
                                            showSaveButton={!Formatters.getBool(this.state, 'showPanelScenarios')}
                                            showOptimizeButton={!Formatters.getBool(this.state, 'showPanelScenarios')}
                                            handleOptimizeButton={(e) => this.setState({ modalOptimizeBusy: true }, () => this.handleOptimizeButton(e))}
                                            handleSaveButton={(e) => this.handleChartTabsSaveButton(e)}
                                            handleRevertButton={(e) => this.handleRevertScenarioButton(e)}
                                            numFormChanges={this.getFormChangesCount()}
                                            scenarioLabel={this.getScenarioLabel(formData)}
                                        />
                                    </Col>
                                </Row>

                            </WhiteBoxPageSection>

                            {sectionInventoryGrids}
                        </Col>
                    </Row>
                </div>
            );

            const endRender = new Date().getTime();

            if (enableTracing) { console.log(`Render time: ${endRender - startRender}ms`); }
        }

        const { ...rest } = this.props;

        let associationVar = this.props.association;
        let company = _.get(associationVar, 'company');

        return (
            <AppComp.RenderPageWithLayout branding={company} pageName={pageTitle} {...rest}>
                {page}
            </AppComp.RenderPageWithLayout>
        );
    }
}

const getApiUrl = props => ApiEndpoints.getReportForAssociationUrl(props.match.params.associationId);

const mapPropsToConfigs = (props) => ReduxQueries.Queries.getReportForAssociationRequest(
    getApiUrl(props),
    ReduxQueries.PropsAccessToken(props)
);

const mapStateToProps = (state, props) => {
    const associationId = _.get(props, 'match.params.associationId', 0);

    return ReduxQueries.MapQueryStateToPropsByUrl(getApiUrl(props), state, props, {
        associationId: associationId,
        association: state.entities.reportAssociation,
        currentOrder: state.entities.reportAssociationCurrentOrder,
        currentOrderChart: state.entities.reportAssociationCurrentOrderChart,
        inventory: state.entities.reportAssociationInventory,
        scenarios: state.entities.reportAssociationScenarios,
        visibleScenarios: state.entities.reportAssociationVisibleScenarios,
        portalInvites: state.entities.reportAssociationPortalInvites,
    });
};

const mapDispatchToProps = {
    toggleClientInvite: MutateActions.toggleClientInviteMutateAsync,
    resendClientInvite: MutateActions.resendClientInviteMutateAsync,
    copyReportScenario: MutateActions.copyReportScenarioMutateAsync,
    deleteReportScenario: MutateActions.deleteReportScenarioMutateAsync,
    updateReportScenario: MutateActions.updateReportScenarioMutateAsync,
    navigateTo: (path) => push(path)
};

const PortalAssociationReportContainer = compose<any>(
    connect(mapStateToProps, mapDispatchToProps),
    connectRequest(mapPropsToConfigs)
)(PortalAssociationReport);

export default withRouter(PortalAssociationReportContainer);



// WEBPACK FOOTER //
// ./src/report/portal.tsx